import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router';
import { EmptyState, Skeleton, cn, useI18n } from '@xdocs/ui';
import { useAuth } from '../../auth/AuthProvider.js';
import { PublicShareReader, useViewerRole } from '../../features/sharing/index.js';
import { useLinkResolve } from './useLinkResolve.js';
import { useSharesT } from './useSharesT.js';
import styles from './LinkResolvePage.module.css';

/**
 * `/s/:token` (F63) — bearer capability URL, accessible WITHOUT auth, NOT
 * moderation-gated (only the `public` path is, §15.3). Deliberately does
 * NOT import `RequireAuth` — this dir's `WIRING.md` documents that the app
 * router must mount this route OUTSIDE `<RequireAuth>`.
 */
export function LinkResolvePage(): ReactElement {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t: tGlobal } = useI18n();
  const { t } = useSharesT();
  const { userId } = useAuth();
  const viewerRole = useViewerRole();
  const { view, isLoading, isError, error, refetch } = useLinkResolve(token ?? '');

  if (!token) {
    return <EmptyState kind="error" title={t('shares:link.notFound.title')} />;
  }

  if (isLoading) {
    return (
      <div className={cn(styles['page'])} aria-busy="true">
        <Skeleton shape="text-line" width="40%" />
        <Skeleton shape="card" count={1} height="200px" />
        <Skeleton shape="text-line" count={3} />
      </div>
    );
  }

  if (isError || !view) {
    if (error && error.code !== 'NOT_FOUND') {
      return <EmptyState kind="error" title={t('shares:link.notFound.title')} action={{ label: tGlobal('common:action.retry'), onClick: refetch }} />;
    }
    // Revoked/expired/unknown token — friendly access-denied gate, not a
    // dead screen (F66 AC).
    return (
      <EmptyState
        kind="error"
        title={t('shares:link.notFound.title')}
        description={t('shares:link.notFound.description')}
        action={{ label: t('shares:link.notFound.cta'), onClick: () => void navigate('/') }}
        mascotAlt=""
      />
    );
  }

  return (
    <PublicShareReader
      view={view}
      viewerUserId={userId}
      viewerRole={viewerRole}
      fullReaderTo={`/read/${view.documentId}`}
      onTakenDown={() => void navigate('/')}
    />
  );
}
